import clsx from 'clsx'
import { useState } from 'react'
import { GetStaticProps } from 'next'

/* blocks */
import Layout from '@/cmp/templates/Layout/BlogLayout'
import BlogPageHero from '@/molecules/BlogPageHero/BlogPageHero'
import BlogCard from '@/molecules/BlogCard/BlogCard'
import PepperVideoLibrary from '@/cmp/organism/PepperVideoLibrary/PepperVideoLibrary'
import ContentMarketingQuotes from '@/cmp/organism/ContentMarketingQuotes/ContentMarketingQuotes'
import BlogNewsletter from '@/cmp/organism/BlogNewsletter/BlogNewsletter'
import CategorySelection from '@/templates/CategorySelection/CategorySelection'
import CMLPromotionalFooter from '@/cmp/organism/CMLPromotionalFooter/CMLPromotionalFooter'
import FooterCta from '@/cmp/organism/FooterCta/FooterCta'

/* utils */
import categories from '@/utils/enums/categories'
import { fetchListingData } from '@/utils/api/posts'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import { blogPageQuery } from '@/utils/queries/blog_list'
import { TEN_MINS_REVALIDATION } from '@/utils/constants/revalidation'

/* assets */
import { ReactComponent as NotificationIcon } from 'public/assets/icons/blog/notification.svg'
import { ReactComponent as MostReadBlogsIcon } from 'public/assets/icons/blog/most-read.svg'
import { ReactComponent as LatestBlogsIcon } from 'public/assets/icons/blog/latest-blogs.svg'
import { ReactComponent as LoaderIcon } from 'public/assets/icons/loader.svg'

interface Props {
  data: BlogListingResponse
  featuredPost?: BlogPostList | null
  mostReadPosts: BlogPostList[]
  latestPosts: BlogPostList[]
  blogPage: BlogPage
}

export default function BlogListing({
  data,
  featuredPost,
  mostReadPosts,
  latestPosts,
  blogPage
}: Props) {
  const [filters, setFilters] = useState<{ [key: string]: string | undefined }>(
    {
      categoryName: ''
    }
  )
  const {
    data: blogListingData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['blog posts', data.posts.pageInfo.startCursor, filters.categoryName],
    ({ pageParam }) =>
      fetchListingData({
        after: pageParam,
        fetchFeaturedPost: true,
        categoryName: filters.categoryName
      }),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      getNextPageParam: lastPage =>
        lastPage.posts.pageInfo.hasNextPage
          ? lastPage.posts.pageInfo.endCursor
          : false
    }
  )

  const postGroup = blogListingData?.pages.map(data => data.posts.nodes) ?? []
  const posts = ([] as BlogPostList[]).concat(...postGroup)

  const handleCategoryClick = async (categoryName: string | undefined) => {
    setFilters({ categoryName })
  }

  const metadata = [
    {
      property: 'description',
      content:
        "Stay ahead in the content game. Pepper Content's insights, information, and expert tips help make you a Master Blogger."
    },
    {
      property: 'og:title',
      content: 'Blog | Pepper Content'
    },
    {
      property: 'og:description',
      content:
        "Stay ahead in the content game. Pepper Content's insights, information, and expert tips help make you a Master Blogger."
    }
  ]

  return (
    <div>
      <Layout title="Blog | Pepper Content" meta={metadata} canonical="/blog/">
        <BlogPageHero
          title={blogPage.heroTitle}
          description={blogPage.heroDescription}
        />

        <section className="container font-body | relative | py-5 md:py-9">
          <div className="flex items-center gap-2 | font-heading uppercase text-sm lg:text-xl text-blue-web-50 font-semibold | mb-4">
            <NotificationIcon />
            Featured Blog
          </div>
          <article>
            {featuredPost ? (
              <BlogCard
                type="featured"
                date={featuredPost.date}
                description={featuredPost.excerpt}
                image={
                  featuredPost.featuredImage
                    ? {
                        url: featuredPost.featuredImage?.node.sourceUrl,
                        altText: featuredPost.featuredImage?.node.altText,
                        width:
                          featuredPost.featuredImage?.node.mediaDetails.width,
                        height:
                          featuredPost.featuredImage?.node.mediaDetails.height
                      }
                    : null
                }
                alt={featuredPost.featuredImage?.node.altText}
                slug={`/blog/${featuredPost.slug}/`}
                categories={featuredPost.categories.nodes}
                title={featuredPost.title}
                isFirstFold={true}
                readTime={featuredPost.readingTime}
              />
            ) : null}
          </article>
        </section>

        <section className="container | relative | py-5 md:py-9">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-20">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-2 | font-heading uppercase text-sm md:text-xl text-blue-web-50 font-semibold | border-b-2 pb-2 lg:mb-5">
                <MostReadBlogsIcon />
                Most read
              </div>
              {mostReadPosts.map(post => (
                <BlogCard
                  key={post.id}
                  type="minimal"
                  title={post.title}
                  description={post.excerpt}
                  categories={post.categories.nodes}
                  authorName={post.author.node.name}
                  date={post.date}
                  slug={post.slug}
                  readTime={post.readingTime}
                />
              ))}
            </div>

            <div className="lg:col-span-6">
              <div className="flex items-center gap-2 | font-heading uppercase text-sm md:text-xl text-blue-web-50 font-semibold | border-b-2 pb-2 lg:mb-4">
                <LatestBlogsIcon />
                Latest Blogs
              </div>
              {latestPosts.map(post => (
                <BlogCard
                  key={post.id}
                  type="minimal"
                  title={post.title}
                  description={post.excerpt}
                  categories={post.categories.nodes}
                  authorName={post.author.node.name}
                  date={post.date}
                  slug={post.slug}
                  readTime={post.readingTime}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="container">
          <div className="font-heading text-3xl lg:text-4xl font-semibold | mb-5">
            Content Marketing
          </div>
          <div className="flex flex-wrap gap-4 md:gap-6 | text-small md:text-base | mb-6 md:mb-11">
            <button
              onClick={() => handleCategoryClick('')}
              className={clsx(
                'bg-gray-20 | px-2 py-1 lg:px-3 lg:py-2 | rounded-full',
                filters.categoryName === '' && 'text-white bg-blue-web-40'
              )}
            >
              All Posts
            </button>
            {Object.values(categories).map(category => {
              return (
                <button
                  key={category?.name}
                  onClick={() => handleCategoryClick(category?.name)}
                  className={clsx(
                    'flex lg:flex-grow-0 justify-center lg:justify-start gap-3 items-center | hover:text-white bg-gray-20 hover:bg-blue-web-40 transition-colors duration-200 ease-in-out | rounded-full px-2 py-1 lg:px-3 lg:py-2',
                    filters.categoryName === category?.name &&
                      'text-white bg-blue-web-40'
                  )}
                >
                  {category?.name}
                </button>
              )
            })}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-5 md:gap-x-8 gap-y-8 md:gap-y-10 | mb-12 lg:mb-16">
            {posts.length !== 0 ? (
              posts.map(post => (
                <article key={post.id}>
                  <BlogCard
                    type="default"
                    date={post.date}
                    description={post.excerpt}
                    image={
                      post.featuredImage
                        ? {
                            url: post.featuredImage.node.sourceUrl,
                            altText: post.featuredImage.node.altText,
                            width: post.featuredImage.node.mediaDetails.width,
                            height: post.featuredImage.node.mediaDetails.height
                          }
                        : undefined
                    }
                    alt={post.featuredImage?.node.altText}
                    slug={`/blog/${post.slug}/`}
                    categories={post.categories.nodes}
                    title={post.title}
                    readTime={post.readingTime}
                  />
                </article>
              ))
            ) : (
              <div className="col-span-full flex items-center justify-center | text-blue-web-40">
                Loading...
                <LoaderIcon className="animate-spin | ml-2" />
              </div>
            )}
          </div>

          {hasNextPage && (
            <div className="flex justify-end items-center">
              <hr className="w-full text-blue-web-50" />
              <button
                onClick={() => fetchNextPage()}
                className="text-blue-web-50 font-medium | flex flex-nowrap flex-grow flex-shrink-0 | bg-transparent rounded-md | pl-2 pr-0"
              >
                Load more articles
                {isFetchingNextPage && (
                  <LoaderIcon className="animate-spin | ml-2" />
                )}
              </button>
            </div>
          )}
        </section>

        {/* TODO: Add the component after getting data */}
        {/* <section className="container font-body | py-5 md:py-9">
          <article>
            {featuredPost ? (
              <PromotionalBanner
                date={featuredPost.date}
                image={
                  featuredPost.featuredImage
                    ? {
                        url: featuredPost.featuredImage?.node.sourceUrl,
                        altText: featuredPost.featuredImage?.node.altText,
                        width:
                          featuredPost.featuredImage?.node.mediaDetails.width,
                        height:
                          featuredPost.featuredImage?.node.mediaDetails.height
                      }
                    : null
                }
                alt={featuredPost.featuredImage?.node.altText}
                slug={`/blog/${featuredPost.slug}/`}
                categories={featuredPost.categories.nodes}
                title={featuredPost.title}
                isFirstFold={true}
                isNew={true}
                bgColor="#7566FF"
                readTime={featuredPost.readingTime}
              />
            ) : null}
          </article>
        </section> */}

        <PepperVideoLibrary
          title={blogPage.videoLibraryTitle}
          description={blogPage.videoLibraryDescription}
          lists={blogPage.videoLibraryList.map(i => ({
            id: i.id,
            title: i.title,
            desc: i.description,
            uploadDate: i.uploadDate,
            isNew: i.isNew,
            url: i.url
          }))}
        />

        <ContentMarketingQuotes
          title="Google only loves you when everyone else loves you first."
          bgColor="#BCF5D7"
        />
        <BlogNewsletter
          title="Get your hands on the latest news!"
          description="Subscribe to our newsletter and stay informed about the latest trends, industry insights, exciting updates, and exclusive offers delivered directly to your inbox."
          bgColor="#FF6333"
        />
        <CategorySelection />
        <CMLPromotionalFooter />
        <FooterCta
          headline="Ready to pivot your content performance?"
          mode="full_width"
          variant="blue"
          primaryCta={{
            id: '1',
            title: 'Book A Demo',
            href: '/book-a-demo/',
            target: '_self'
          }}
          secondaryCta={{
            id: '2',
            title: 'Sign Up',
            href: 'https://business.peppercontent.io/signup',
            target: '_self'
          }}
        />
      </Layout>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const result = await fetchListingData({
    fetchFeaturedPost: true,
    mostReadTag: 'most-read',
    limit: 3
  })

  const blogPageResult = await getStrapiGraphqlData<BlogPageResponse>(
    blogPageQuery.loc?.source.body
  )

  return {
    props: {
      data: result,
      posts: result.posts.nodes,
      featuredPost: result.featuredPost.nodes?.[0] ?? null,
      mostReadPosts: result.mostReadPosts.nodes,
      latestPosts: result.latestPosts.nodes,
      blogPage: blogPageResult.blogPage.data.attributes
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}
